import { useParams, Link, useLocation } from 'react-router-dom'
import { Calendar, Tag, ArrowLeft, Share2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useBlogPost, useBlogPosts } from '../hooks/useBlog'
import { SEOHead } from '../components/SEOHead'
import { formatDate } from '../utils/helpers'
import { useState, useEffect } from 'react'
import { Breadcrumbs, generateBreadcrumbs } from '../components/Breadcrumbs'
import { RelatedPosts } from '../components/RelatedPosts'
import { PostNavigation } from '../components/PostNavigation'

// Progress bar component
function ReadingProgress() {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const scrollPercent = (scrollTop / docHeight) * 100
      setProgress(Math.min(100, Math.max(0, scrollPercent)))
    }

    window.addEventListener('scroll', updateProgress)
    updateProgress() // Initial calculation

    return () => window.removeEventListener('scroll', updateProgress)
  }, [])

  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
      <div
        className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const { post, loading, error } = useBlogPost(slug || '')
  const { posts: allPosts } = useBlogPosts({ per_page: 100 }) // Get all posts for related/navigation

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <ReadingProgress />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading blog post...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white">
        <ReadingProgress />
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-semibold text-red-600 mb-4">
              Post Not Found
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'The blog post you are looking for does not exist.'}
            </p>
            <Link to="/blog" className="btn-primary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || post.title,
          url: window.location.href,
        })
      } catch (err) {
        // User cancelled sharing
      }
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href)
      // You could show a toast notification here
    }
  }

  return (
    <>
      {post && (
        <SEOHead
          title={`${post.title} | llm.txt Generator Blog`}
          description={post.meta_description || post.excerpt || `Read about ${post.title} on the llm.txt Generator blog.`}
          keywords={post.tags ? post.tags.join(', ') : 'AI SEO, llm.txt'}
          canonical={`https://llmtext.net/blog/${post.slug}`}
          type="article"
        />
      )}
      <div className="min-h-screen bg-white">
        <ReadingProgress />

        {/* Navigation */}
        <div className="sticky top-1 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-40">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link
                to="/blog"
                className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>

              {post && (
                <Breadcrumbs
                  items={generateBreadcrumbs(location.pathname, post.title)}
                  className="hidden md:flex"
                />
              )}
            </div>
          </div>
        </div>

        {/* Blog Post */}
        <article className="max-w-5xl mx-auto px-6 py-12">
          {/* Post Header */}
          <header className="mb-16 text-center">
            {post.featured && (
              <div className="mb-6">
                <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-primary-500 to-primary-600 text-white">
                  Featured Post
                </span>
              </div>
            )}

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  {formatDate(post.created_at)}
                </div>
                {post.category && (
                  <div className="flex items-center">
                    <Tag className="h-4 w-4 mr-2" />
                    {post.category}
                  </div>
                )}
              </div>

              <button
                onClick={handleShare}
                className="inline-flex items-center text-gray-500 hover:text-primary-600 transition-colors duration-200"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </button>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-6">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors duration-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </header>

          {/* Featured Image */}
          {post.hero_image && (
            <div className="mb-12 rounded-2xl overflow-hidden shadow-xl border border-gray-100">
              <img
                src={post.hero_image}
                alt={post.title}
                className="w-full h-auto object-cover max-h-[500px]"
              />
            </div>
          )}

          {/* Post Content */}
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[]}
              components={{
                h1: ({ children }) => (
                  <h1 className="text-3xl font-bold font-heading text-gray-900 mt-8 mb-4 first:mt-0">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="text-2xl font-semibold font-heading text-gray-900 mt-8 mb-4">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl font-semibold font-heading text-gray-900 mt-6 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {children}
                  </p>
                ),
                img: ({ src, alt }) => (
                  <img 
                    src={src} 
                    alt={alt} 
                    className="rounded-2xl shadow-xl border border-gray-100 w-full h-auto my-8" 
                  />
                ),
                ul: ({ children }) => (
                  <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside text-gray-700 mb-4 space-y-2">
                    {children}
                  </ol>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary-500 pl-4 italic text-gray-600 my-6">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm">
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg overflow-x-auto mb-4">
                    {children}
                  </pre>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    className="text-primary-600 hover:text-primary-700 underline"
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {children}
                  </a>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-lg">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-gray-50">
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="bg-white divide-y divide-gray-200">
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr className="hover:bg-gray-50">
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-b border-gray-200">
                    {children}
                  </td>
                ),
              }}
            >
              {typeof post.content === 'string' ? post.content : ''}
            </ReactMarkdown>
          </div>

          {/* Post Footer */}
          <footer className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Last updated: {formatDate(post.updated_at)}
              </div>

              <button
                onClick={handleShare}
                className="btn-outline"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share Post
              </button>
            </div>
          </footer>
        </article>

        {/* CTA Section */}
        <div className="mt-16 p-8 bg-gradient-to-r from-primary-50 to-primary-100 rounded-2xl border border-primary-200">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-2xl font-semibold text-primary-900 mb-4">
              Ready to optimize your website for AI?
            </h3>
            <p className="text-primary-700 mb-6 text-lg">
              Use our free llm.txt generator to create your file in minutes and improve your AI discoverability.
            </p>
            <Link to="/generator" className="btn-primary btn-wide text-lg py-3">
              Create Your llm.txt
            </Link>
          </div>
        </div>

        {/* Post Navigation */}
        {allPosts.length > 0 && (
          <PostNavigation currentPost={post} allPosts={allPosts} />
        )}

        {/* Related Posts */}
        {allPosts.length > 0 && (
          <RelatedPosts currentPost={post} allPosts={allPosts} />
        )}
      </div>
    </>
  )
}
